import Button from "components/form/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import PayPalImg from "icons/payments/PayPal";
import InputField from "components/form/InputField";
import MobileInputNew from "components/form/MobileInputNew";
import {
  checkPhone,
  formatMinAndMaxAmount,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { PhoneInput } from "types/common";
import { PaymentGateway } from "types/payments";

declare global {
  interface Window {
    StaxJs: any;
  }
}

const PayPalForm = ({
  amount,
  isAgreement,
  isSchedulePayment,
  isRetry,
  isUpdatePlan,
  isEdit,
  isProductLink,
  collectPhone,
  phoneNumber,
  handlePhoneCountryChange,
  handleOnPhoneChange,
  email,
  setEmail,
  handleEmailBlur = () => {},
  userData,
  handleBack,
  createPayPalIntent,
  amtErr,
  isStaxPayPalLoading = false,
  verifyStaxPayPal = async ({
    transactionId = "",
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => {
    return Promise.resolve();
  },
}: {
  amount: string;
  isAgreement?: boolean;
  isSchedulePayment?: boolean;
  isRetry?: boolean;
  isUpdatePlan?: boolean;
  isEdit?: boolean;
  isProductLink?: boolean;
  collectPhone?: boolean;
  phoneNumber: PhoneInput;
  handlePhoneCountryChange: (countryCode: string) => void;
  handleOnPhoneChange: (phone: string) => void;
  email: string;
  setEmail: (value: string) => void;
  userData: any;
  createPayPalIntent: () => Promise<void>;
  handleEmailBlur?: () => void;
  handleBack: () => void;
  amtErr: string | null;
  isStaxPayPalLoading?: boolean;
  verifyStaxPayPal?: ({
    transactionId,
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => Promise<void>;
}) => {
  const [isPayPalLoding, setIsPayPalLoading] = useState<boolean>(false);

  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );

  const { isChecking, isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );

  const shouldUseStandardPayPal = useMemo(
    () =>
      isSchedulePayment ||
      isAgreement ||
      paymentPermission?.pp_gateway_name?.name !== PaymentGateway.STAX,
    [isSchedulePayment, isAgreement, paymentPermission]
  );

  useEffect(() => {
    if (shouldUseStandardPayPal) {
      const badge = document.querySelector(".grecaptcha-badge") as HTMLElement;
      if (badge && badge instanceof HTMLElement) {
        badge.style.display = "none";
      }
    }
  }, [shouldUseStandardPayPal]);

  return shouldUseStandardPayPal ? (
    <>
      {isAgreement ? (
        <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
          <Button
            text={`Back`}
            variant="white"
            onClick={handleBack}
            className="lg:w-40 w-full max-w-40"
          />
          <div
            className={`lg:w-40 w-full flex justify-center items-center py-3 rounded-md max-w-40 ${
              parseFloat(amount) >= 1.0 && !amtErr
                ? "cursor-pointer bg-yellow3 hover:!bg-btnYellowHover"
                : "cursor-not-allowed pointer-events-none bg-btnYellowDisable"
            }`}
            onClick={async () => {
              if (parseFloat(amount) >= 1.0 && !amtErr) {
                setIsPayPalLoading(true);
                await createPayPalIntent();
                setIsPayPalLoading(false);
              }
            }}
          >
            {isPayPalLoding ? (
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            ) : (
              <>
                <PayPalImg />
              </>
            )}
          </div>
        </div>
      ) : isSchedulePayment && !isRetry ? (
        <>
          <div
            className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5"
          >
            <div
              className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
            >
              {isUpdatePlan && (
                <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
              )}

              <div className="w-full flex justify-between gap-5">
                <Button
                  className="lg:!w-40 !w-full"
                  isDisabled={false}
                  text="Back"
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                />
                <div
                  className={`lg:w-40 w-full flex justify-center items-center py-3 rounded-md ${
                    parseFloat(amount) >= 1.0 && !amtErr && !isEdit
                      ? "cursor-pointer bg-yellow3 hover:!bg-btnYellowHover"
                      : "cursor-not-allowed pointer-events-none bg-btnYellowDisable"
                  }`}
                  onClick={async () => {
                    if (parseFloat(amount) >= 1.0 && !amtErr && !isEdit) {
                      setIsPayPalLoading(true);
                      await createPayPalIntent();
                      setIsPayPalLoading(false);
                    }
                  }}
                >
                  {isPayPalLoding ? (
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  ) : (
                    <>
                      <PayPalImg />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {isProductLink && (
            <div className="pb-4">
              <InputField
                showLabel={true}
                label="EMAIL"
                name="email"
                type="text"
                placeholder="Email address"
                onBlur={handleEmailBlur}
                onChange={(e) => {
                  setEmail(
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : e.target.value
                  );
                }}
                value={
                  userData?.data?.accountData?.email
                    ? userData?.data?.accountData?.email
                    : email
                }
                disabled={userData?.data?.accountData?.email}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
                _className="!text-xs !font-bold !leading-normal"
              />

              {collectPhone && (
                <div className="pt-5">
                  <div
                    className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                  >
                    Phone
                  </div>
                  <MobileInputNew
                    inputValue={phoneNumber?.phone || ""}
                    isProductLinkInput={true}
                    userCountry={
                      getCountryShortName(phoneNumber?.country_code) || "US"
                    }
                    setCountry={handlePhoneCountryChange}
                    setInputValue={handleOnPhoneChange}
                  />
                </div>
              )}
            </div>
          )}

          <div
            className={`w-full ${
              isRetry ? "flex flex-col items-end justify-end" : ""
            }`}
          >
            {!isUpdatePlan && (
              <div
                className={`${
                  isRetry ? "w-fit px-14 !py-4" : "w-full"
                } flex justify-center py-3 rounded-md ${
                  parseFloat(amount) < 1.0 ||
                  amtErr ||
                  isEdit ||
                  (isProductLink &&
                    (!validateEmail(email) || !isSubscriptionAllowed)) ||
                  (collectPhone && !checkPhone(phoneNumber))
                    ? "cursor-not-allowed pointer-events-none bg-btnYellowDisable"
                    : "cursor-pointer bg-yellow3 hover:!bg-btnYellowHover"
                }`}
                onClick={async () => {
                  if (
                    (parseFloat(amount) >= 1.0 && !amtErr && !isEdit) ||
                    (isProductLink &&
                      validateEmail(email) &&
                      email?.trim() !== "" &&
                      isSubscriptionAllowed &&
                      !isChecking)
                  ) {
                    setIsPayPalLoading(true);
                    await createPayPalIntent();
                    setIsPayPalLoading(false);
                  }
                }}
              >
                {isPayPalLoding ? (
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                ) : (
                  <>
                    <PayPalImg />
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  ) : (
    <>
      <StaxPayPalForm
        amount={parseFloat(amount)}
        isEdit={isEdit}
        userData={userData}
        amtErr={amtErr}
        verifyStaxPayPal={verifyStaxPayPal}
        isStaxPayPalLoading={isStaxPayPalLoading}
      />
    </>
  );
};

interface StaxPayPalProps {
  amount: number;
  isEdit?: boolean;
  userData?: any;
  amtErr: string | null;
  isStaxPayPalLoading?: boolean;
  verifyStaxPayPal?: ({
    transactionId,
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => Promise<void>;
}

const StaxPayPalForm = ({
  amount,
  isEdit,
  amtErr,
  verifyStaxPayPal,
  userData,
  isStaxPayPalLoading,
}: StaxPayPalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const staxInstanceRef = useRef<any>(null);
  const [isButtonsRendered, setIsButtonsRendered] = React.useState(false);
  const [isButtonsRendering, setIsButtonsRendering] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const { paymentPermission, generalSettings } = useAppSelector(
    (state: RootState) => state.payment
  );
  const paymentCompletedRef = useRef(false);
  const orderCreatedRef = useRef(false);

  const scriptLoaded = useRef<boolean>(false);
  const [isScriptReady, setIsScriptReady] = React.useState(false);

  useEffect(() => {
    if (window.StaxJs) {
      setIsScriptReady(true);
      scriptLoaded.current = true;
      return;
    }

    if (scriptLoaded.current) return;

    const existingScript = document.querySelector(
      'script[src="https://staxjs.staxpayments.com/staxjs-captcha.js"]'
    ) as HTMLScriptElement | null;

    if (existingScript) {
      const onLoad = () => {
        setIsScriptReady(true);
        console.log("Product Link ✅ StaxJs script loaded");
      };

      existingScript.addEventListener("load", onLoad);
      scriptLoaded.current = true;

      return () => {
        existingScript.removeEventListener("load", onLoad);
      };
    }

    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/staxjs-captcha.js";
    script.async = true;

    document.body.appendChild(script);

    script.onload = () => {
      setIsScriptReady(true);
      console.log("Product Link ✅ StaxJs script loaded");
    };

    script.onerror = () => {
      console.error("Product Link ❌ Failed to load StaxJs script");
    };

    scriptLoaded.current = true;

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const amountLimit = useMemo(() => {
    if (
      userData?.data?.accountData?.balance &&
      generalSettings?.systemSettings
    ) {
      const { min, max } = formatMinAndMaxAmount(
        generalSettings?.systemSettings,
        userData?.data?.accountData?.balance
      );

      return { min, max };
    }

    return { min: 1.0, max: 100000.0 };
  }, [generalSettings, userData]);

  const handleBadgeDisplay = (show?: boolean) => {
    const badge = document.querySelector(".grecaptcha-badge");
    if (badge && badge instanceof HTMLElement) {
      badge.style.display = show ? "block" : "none";
    }
  };

  useEffect(() => {
    if (!isScriptReady || !window.StaxJs) {
      staxInstanceRef.current = null;
      console.log("Product Link ⏳ Waiting for StaxJs...");
      handleBadgeDisplay();
      return;
    }

    // 🚫 Prevent double init (StrictMode safe)
    if (staxInstanceRef.current) return;

    if (!paymentPermission?.pp_gateway_name?.key) {
      handleBadgeDisplay();
      staxInstanceRef.current = null;
      setErr("Payment cannot be processed at this time.");
      return;
    }

    if (isEdit || amtErr) {
      setIsButtonsRendering(false);
      staxInstanceRef.current = null;
      handleBadgeDisplay();
      return;
    }


    if (amount < amountLimit.min || amount > amountLimit.max) {
      setIsButtonsRendering(false);
      staxInstanceRef.current = null;
      handleBadgeDisplay();
      console.log(
        `Amount must be between ${amountLimit.min} and ${amountLimit.max} to initialize PayPal buttons`
      );
      return;
    }


    // All checks passed, initialize PayPal buttons
    setIsButtonsRendering(true);
    const webpaymentsToken =
      paymentPermission?.pp_gateway_name?.key ||
      "PayPal-Test-Merchant-136cf5cf8f62";
    const staxJsConfig = {};

    const staxjs = new window.StaxJs(webpaymentsToken, staxJsConfig);
    staxInstanceRef.current = staxjs;

    staxjs.setTotal(amount);

    const paypalConfig = {
      selector: "#paypal-button-container",
      enableFunding: [],
      invoiceId: "",
      currency: "USD",
    };

    staxjs
      .showPayPalButtons(paypalConfig)
      .then(() => {
        console.log("Product Link form was loaded");
        console.log("Product Link paypalConfig:", paypalConfig);
      })
      .catch((err: any) => {
        staxInstanceRef.current = null;
        setErr("There was an error loading the PayPal form. Please try again.");
        console.log("Product Link there was an error loading the form:", err);
      });

    staxjs.on("paypal_buttons_rendered", function () {
      console.log("Product Link Buttons rendered");
      staxInstanceRef.current = null;
      setErr(null);
      setIsButtonsRendering(false);
      setIsButtonsRendered(true);
      handleBadgeDisplay(true);
    });

    staxjs.on("paypal_order_create_success", function () {
      console.log("Product Link PayPal order created successfully");
      orderCreatedRef.current = true;
    });

    staxjs.on("paypal_order_create_error", function (err: any) {
      console.log("Product Link PayPal order create error:", err);
      setErr("There was an error creating the order. Please try again.");
      orderCreatedRef.current = false;
    });

    staxjs.on("paypal_order_payment_success", function (invoice: any) {
      console.log("Product Link ✅ Payment Success:", invoice);

      const transactions = invoice?.child_transactions || [];

      if (transactions.length > 0) {
        setErr(null);
        if (verifyStaxPayPal) {
          verifyStaxPayPal({ transactionId: transactions?.[0]?.id });
          staxInstanceRef.current = null;
        }
      } else {
        staxInstanceRef.current = null;
        setErr(
          "Payment was successful but no transaction details were found. Please contact support with your invoice ID: " +
            invoice.id
        );
      }

      paymentCompletedRef.current = true;

      handleBadgeDisplay();
    });

    staxjs.on("paypal_error", function (err: any) {
      console.log("PayPal error:", err);
    });

    staxjs.on("paypal_order_payment_error", function (err: any) {
      console.log("Payment error:", err);
      staxInstanceRef.current = null;
      setErr("There was an error processing the payment. Please try again.");
      handleBadgeDisplay();
      paymentCompletedRef.current = false;
    });

    return () => {
      // Optional cleanup if needed
      staxInstanceRef.current = null;
    };
  }, [amount, amtErr, isEdit, isScriptReady, paymentPermission, scriptLoaded]);



  return (
    <>
      <div
        id="paypal-wrapper"
        className={
          isButtonsRendered && !isStaxPayPalLoading ? "block" : "hidden"
        }
      >
        <div id="paypal-button-container" ref={containerRef}></div>
      </div>

      <div
        className={`w-full ${isButtonsRendered && !isStaxPayPalLoading ? "hidden" : "block"}`}
      >
        <div
          className={`w-full flex justify-center items-center py-3 rounded-md cursor-not-allowed pointer-events-none bg-btnYellowDisable`}
        >
          {isButtonsRendering || isStaxPayPalLoading ? (
            <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          ) : (
            <PayPalImg />
          )}
        </div>
      </div>
      {err && <div className="text-xs text-red">{err}</div>}
    </>
  );
};

export default PayPalForm;
